import { useEffect, useRef, useState } from "react";
import { ImageLoader } from "../ImageLoader";

import weightIcon from "../../assets/icons/measure/weight.png";
import umbilicalFemaleIcon from "../../assets/icons/measure/umbilical-female.png";
import umbilicalMaleIcon from "../../assets/icons/measure/umbilical-male.png";

/** 1x1 transparent GIF — suppresses the WebView's own oversized play placeholder. */
const BLANK_POSTER =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/**
 * Draws one Story's content inside the 9:16 stage.
 *
 * Two families, which is the mockups' best idea and worth keeping:
 *
 *   photo   — a manual photo, or an automatic cover (Movimento, Ricetta,
 *             Sfida): heavy white type over the image behind a scrim.
 *   metric  — Progressi (peso, circonferenza): a light neutral ground with a
 *             white data card, because a number deserves to be read, not
 *             fought for over a photograph.
 *
 * Everything is rendered in Roboto on the app's palette rather than the
 * mockups' serif and handwritten faces, so Stories sit with the rest of the app.
 *
 * The backend sends only values that exist. `template` shape (all optional):
 *
 *   { label, tone, title, subtitle, meta, chips: [], stars,
 *     metric: { value, unit, description } }
 */

export const TONES = {
  movimento: "#7C4DFF",
  ricetta: "var(--ion-color-primary)",
  sfida: "var(--ion-color-secondary)",
  progressi: "var(--ion-color-primary)",
};

/**
 * The Misure page's own glyphs, so a Progressi Story shows the member the same
 * scale and measuring tape they entered the reading with.
 *
 * The backend names the icon because the umbilical one is a torso and has to
 * follow the AUTHOR's sex — reading the viewer's would put the wrong body on
 * someone else's Story. `unit` is the fallback for Stories written before the
 * backend started sending a name.
 */
const METRIC_ICONS = {
  weight: weightIcon,
  "umbilical-female": umbilicalFemaleIcon,
  "umbilical-male": umbilicalMaleIcon,
};

export const metricIconFor = (template) =>
  METRIC_ICONS[template?.icon] ??
  (template?.metric?.unit === "kg" ? weightIcon : umbilicalFemaleIcon);

const CAPTION_BACKGROUNDS = {
  none: "transparent",
  black: "rgba(0, 0, 0, 0.55)",
  white: "rgba(255, 255, 255, 0.9)",
  green: "var(--ion-color-primary)",
};

const captionStyle = (attributes) => {
  const background = CAPTION_BACKGROUNDS[attributes.text_background] ?? "transparent";
  const isLightBackground = attributes.text_background === "white";

  return {
    background,
    color: attributes.text_color || (isLightBackground ? "#1f2937" : "#ffffff"),
    padding: attributes.text_background && attributes.text_background !== "none"
      ? "10px 14px"
      : 0,
    borderRadius: 12,
    textShadow: !attributes.text_background || attributes.text_background === "none"
      ? "0 1px 6px rgba(0,0,0,0.55)"
      : "none",
  };
};

const POSITIONS = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end",
};

const StoryLabel = ({ template }) =>
  template?.label ? (
    <span
      className="story-template__label"
      style={{ background: TONES[template.tone] ?? "rgba(255,255,255,0.22)" }}
    >
      {template.label}
    </span>
  ) : null;

const StoryCaption = ({ attributes }) =>
  attributes.caption ? (
    <div className="story-template__caption-wrap">
      <p className="story-template__caption" style={captionStyle(attributes)}>
        {attributes.caption}
      </p>
    </div>
  ) : null;

export default function StoryTemplate({
  story,
  // Whether this slide is the one being watched. Swiper keeps its neighbours
  // mounted, so a slide has to be told when it stops being the one on screen.
  isActive = true,
  videoRef = null,
  onVideoMeta = null,
  onVideoEnded = null,
}) {
  const attributes = story?.attributes ?? {};
  const template = attributes.template ?? null;
  const isMetric = Boolean(template?.metric);
  const background = attributes.media_url || attributes.background_url || null;
  // Written by the backend at publish time rather than guessed from the URL —
  // an <img> pointed at an .mp4 renders nothing at all, silently.
  const isVideo = attributes.media_type === "video" && Boolean(attributes.media_url);

  const ownVideoRef = useRef(null);
  const backdropRef = useRef(null);
  const [buffering, setBuffering] = useState(true);
  const [mutedBySystem, setMutedBySystem] = useState(false);

  // The viewer wants the same element for its progress bar and hold-to-pause,
  // so hand it on rather than keeping two refs in sync.
  const attachVideo = (element) => {
    ownVideoRef.current = element;
    if (typeof videoRef === "function") videoRef(element);
    else if (videoRef) videoRef.current = element;
  };

  const mediaUrl = isVideo ? attributes.media_url : null;

  // ── Is the picture ready to be looked at? ────────────────────────────────
  const [imageReady, setImageReady] = useState(false);

  useEffect(() => {
    // A clip reports its own readiness through the buffering state below, and a
    // Story with no picture at all — the Progressi card — has nothing to wait
    // for.
    // The viewer times its own wait; this state only drives the spinner.
    if (isVideo || !background) {
      setImageReady(true);
      return undefined;
    }

    setImageReady(false);
    let settled = false;
    const ready = () => {
      if (settled) return;
      settled = true;
      setImageReady(true);
    };

    const image = new Image();
    image.onload = ready;
    // A picture that fails is not a reason to freeze the Story on a spinner
    // forever — show what there is and move on.
    image.onerror = ready;
    image.src = background;

    // Decoded from cache between render and this effect: `complete` is already
    // true and neither handler will fire.
    if (image.complete) ready();

    // ⚠️ The backstop that matters. Contabo has no CDN in front of it and these
    // images already fail with ERR_CONNECTION_RESET under load; without this a
    // stalled request would hold the viewer on a spinner indefinitely.
    const bail = setTimeout(ready, 8000);

    return () => {
      clearTimeout(bail);
      image.onload = null;
      image.onerror = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [background, isVideo]);

  useEffect(() => {
    const video = ownVideoRef.current;
    // Only the Story actually on screen plays: the viewer passes a ref for that
    // one and null for the slides either side.
    if (!video || !mediaUrl || !videoRef) return undefined;

    // Not the slide being watched: never start, and make sure it is silent.
    if (!isActive) {
      video.pause();
      video.muted = true;
      backdropRef.current?.pause?.();
      return undefined;
    }

    let cancelled = false;
    setBuffering(true);

    // Sound first. Opening a Story is a tap, so the platform will often allow
    // it; when it refuses, the promise rejects and we retry muted rather than
    // leave the member looking at a still frame. `autoPlay` alone is not enough
    // on Android, where the WebView blocks it until something calls play().
    // ⚠️ `play()` returns a promise, and pausing while it is pending does NOT
    // stop it: the promise resolves afterwards and the clip is playing again.
    // That is how a Story's audio followed the member into the next Story, and
    // on into other people's. Every await is therefore re-checked, and the
    // element is stopped again if we lost the race.
    const stop = () => {
      video.pause();
      // Muted as well as paused. If anything at all resumes this element — an
      // autoplay attribute, a stray resume, a platform quirk — it must at least
      // be silent, because a Story the member has left must never be heard.
      video.muted = true;
    };

    const start = async () => {
      video.muted = false;
      try {
        await video.play();
        if (cancelled) {
          stop();
          return;
        }
        setMutedBySystem(false);
        return;
      } catch {
        // Refused with sound — fall through.
      }
      if (cancelled) return;
      video.muted = true;
      try {
        await video.play();
        if (cancelled) {
          stop();
          return;
        }
        setMutedBySystem(true);
      } catch {
        // Refused outright; the member can still tap to play.
        if (!cancelled) setBuffering(false);
      }
    };

    start();

    return () => {
      cancelled = true;
      stop();
      // The blurred copy behind it is muted, but it is still a decoder running
      // for a Story nobody is looking at.
      backdropRef.current?.pause?.();
    };
    // ⚠️ `isActive` is a dependency deliberately. Relying on the videoRef prop
    // flipping to null was too subtle: swiping to another author left the
    // previous slide's clip playing WITH SOUND, following the member into other
    // people's Stories. Being told plainly when a slide stops being watched is
    // the only version that holds for every route out — tap, swipe, close and
    // unmount alike.
  }, [mediaUrl, videoRef, isActive]);

  if (isMetric) {
    return (
      <div className="story-template story-template--metric">
        <StoryLabel template={template} />
        <h2 className="story-template__metric-title">{template.title}</h2>

        <div className="story-template__metric-card">
          <img
            className="story-template__metric-icon"
            src={metricIconFor(template)}
            alt=""
            aria-hidden="true"
          />
          <div className="story-template__metric-value">
            {template.metric.value}
            {template.metric.unit ? (
              <span className="story-template__metric-unit">
                {template.metric.unit}
              </span>
            ) : null}
          </div>
          <span className="story-template__metric-rule" />
          {template.metric.description ? (
            <p className="story-template__metric-desc">
              {template.metric.description}
            </p>
          ) : null}
        </div>

        {attributes.caption ? (
          <p className="story-template__metric-caption">{attributes.caption}</p>
        ) : null}
      </div>
    );
  }

  // Movimento gets its own composition: the words go to the right, clear of a
  // trainer who is centred in the frame. `tone` is what the backend already
  // sends to pick the label colour, so nothing new travels for this.
  const tone = template?.tone ?? null;
  const hasTemplateText = Boolean(
    template?.title || template?.subtitle || template?.meta,
  );
  // Two automatic Storie put their headline at the TOP of the frame rather than
  // resting it on the bottom edge: a Sfida, so it reads differently at a glance,
  // and a five-star recipe, whose picture stays 16:9 and leaves the top free.
  // In both the member's own caption stays LOW — between the middle of the frame
  // and the button — which is where a five-star Storia already put it and what
  // the client asked to keep.
  const isTopAnchored =
    hasTemplateText &&
    (tone === "sfida" || attributes.story_type === "recipe_rated");

  return (
    <div
      className={`story-template story-template--photo${
        tone === "movimento" ? " story-template--movimento" : ""
      }${isTopAnchored ? " story-template--top" : ""}`}
    >
      {isVideo ? (
        // Letterboxed rather than cropped, matching the composer's preview, so
        // what the author saw is what everybody sees.
        <>
          {/* The same clip again, blurred and darkened, filling the bands a
              16:9 video leaves in a 9:16 frame. Deliberately NOT kept in sync
              with the one above: it is blurred past recognition, so a few
              frames of drift is invisible, and syncing two decoders would cost
              far more than it could ever be worth. Muted and inert. */}
          <video
            ref={backdropRef}
            className="story-template__backdrop story-template__backdrop--video"
            src={attributes.media_url}
            poster={BLANK_POSTER}
            autoPlay={isActive}
            muted
            loop
            playsInline
            preload="auto"
            tabIndex={-1}
            aria-hidden="true"
          />
          <video
            ref={attachVideo}
            src={attributes.media_url}
            className="story-viewer__media story-viewer__media--video"
            // A transparent 1px poster, deliberately. Without one the Android
            // WebView draws its own placeholder — a play triangle scaled to the
            // whole frame, which is the "ugly thumbnail" that appeared whenever
            // playback had not started yet.
            // The cover the Story was built from, so the frame is never blank
            // while the first video frame is fetched. Falls back to the blank
            // pixel, which exists only to suppress the WebView's own oversized
            // play placeholder.
            poster={attributes.poster_url || BLANK_POSTER}
            autoPlay={isActive}
            muted={!isActive}
            playsInline
            preload="auto"
            onLoadedMetadata={(event) => onVideoMeta?.(event.currentTarget.duration)}
            onTimeUpdate={(event) => {
              // A clip longer than the cap is shown only as far as the cap. The
              // server sends the number; the element is stopped here rather
              // than trusting it to end on its own.
              const limit = Number(attributes.play_seconds);
              if (!Number.isFinite(limit) || limit <= 0) return;
              const video = event.currentTarget;
              if (video.currentTime >= limit) {
                video.pause();
                onVideoEnded?.();
              }
            }}
            onPlaying={() => setBuffering(false)}
            onWaiting={() => setBuffering(true)}
            onStalled={() => setBuffering(true)}
            onEnded={() => onVideoEnded?.()}
          />
          {buffering ? (
            <div className="story-viewer__media-loading" aria-hidden="true">
              <span className="story-viewer__spinner" />
            </div>
          ) : null}
          {mutedBySystem ? (
            // Autoplay with sound was refused, so the clip is running silently.
            // Say so, and let a tap turn it on — the tap is the gesture the
            // platform was waiting for.
            <button
              type="button"
              className="story-viewer__unmute"
              onClick={(event) => {
                event.stopPropagation();
                const video = ownVideoRef.current;
                if (!video) return;
                video.muted = false;
                video.play().catch(() => {});
                setMutedBySystem(false);
              }}
            >
              Tocca per l'audio
            </button>
          ) : null}
        </>
      ) : background ? (
        <>
          {/* The picture is shown WHOLE, so a 4:5 post image is not cropped to
              a face by the 9:16 frame. The bands that leaves are filled with a
              blurred, darkened copy of the same image rather than flat black,
              which reads as deliberate instead of broken. A picture already in
              9:16 covers the frame exactly and never reveals this layer. */}
          <div
            className="story-template__backdrop"
            style={{ backgroundImage: `url("${background}")` }}
            aria-hidden="true"
          />
          {/* A Storia whose picture cannot be fetched shows no picture. The
              generic "no image" icon belongs on a thumbnail, where it explains
              a gap in a list; blown up in the middle of a full-screen frame it
              just looks broken, and the headline reads perfectly well on the
              backdrop alone. */}
          <ImageLoader
            image={background}
            css="story-viewer__media"
            styles={{ width: "100%", height: "100%" }}
            hideOnError
            // A Storia IS its picture, and it has ~5 seconds to show it. It must
            // never queue behind the feed loading underneath the viewer — that
            // is exactly what left this frame showing nothing but the blurred
            // backdrop while the progress bar ran on to the next Story.
            priority
          />
          {imageReady ? null : (
            <div className="story-viewer__media-loading" aria-hidden="true">
              <span className="story-viewer__spinner" />
            </div>
          )}
        </>
      ) : (
        <div className="story-template__no-media" />
      )}

      {/* A template's text block is much taller than a bare caption, so it
          needs the taller gradient or its top half sits on the raw photo. */}
      {/* Top-anchored text needs its own gradient up there — and still needs the
          bottom one, because the caption and the button live down there. The
          tall --template variant is for the other layouts, whose whole text
          block sits at the bottom. */}
      {isTopAnchored ? <div className="story-template__scrim-top-heavy" /> : null}

      <div
        className={`story-viewer__scrim-bottom${
          hasTemplateText && !isTopAnchored
            ? " story-viewer__scrim-bottom--template"
            : ""
        }`}
      />

      <div
        className="story-template__body"
        style={{
          // Set here rather than in CSS because the declared value would lose
          // to this inline one anyway. These layouts ignore text_position: the
          // backend sends "bottom" for every automatic Storia, and the whole
          // point is that these two do not sit there.
          justifyContent: isTopAnchored
            ? "flex-start"
            : POSITIONS[attributes.text_position] ?? "flex-end",
        }}
      >
        <StoryLabel template={template} />

        {template?.title ? (
          <h2 className="story-template__title">{template.title}</h2>
        ) : null}

        {template?.subtitle ? (
          <p className="story-template__subtitle">{template.subtitle}</p>
        ) : null}

        {/* Only ever the real duration, star rating or recipe categories the
            backend could fill — never an invented figure. */}
        {template?.meta ? (
          <p className="story-template__meta">{template.meta}</p>
        ) : null}

        {template?.stars ? (
          <p className="story-template__stars" aria-label={`${template.stars} stelle`}>
            {"★".repeat(template.stars)}
          </p>
        ) : null}

        {Array.isArray(template?.chips) && template.chips.length > 0 ? (
          <div className="story-template__chips">
            {template.chips.map((chip) => (
              <span key={chip} className="story-template__chip">
                {chip}
              </span>
            ))}
          </div>
        ) : null}

        {/* Only when the block is at the bottom already — otherwise the caption
            is drawn low, below. */}
        {isTopAnchored ? null : <StoryCaption attributes={attributes} />}
      </div>

      {/* The member's own words, kept between the middle of the frame and the
          button rather than dragged up with the headline. */}
      {isTopAnchored ? (
        <div className="story-template__caption-low">
          <StoryCaption attributes={attributes} />
        </div>
      ) : null}
    </div>
  );
}
